import { z } from 'zod';

import { SizeSetSystem } from '@nucleus-commerce/api-client/admin';

export const SIZE_SYSTEMS = Object.values(SizeSetSystem);
/** Systems a size can have an equivalent in; `custom` names no scale to convert to. */
export const EQUIVALENT_SYSTEMS = SIZE_SYSTEMS.filter((system) => system !== 'custom');
export const MAX_SIZES = 500;

const KEY = /^[a-z][a-z0-9_]*$/;
/** A store page handle: lowercase, digits and single hyphens, not at either end. */
const HANDLE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const NUMBER = /^-?\d+(\.\d+)?$/;

const isNumber = (v: string) => NUMBER.test(v) && Number.isFinite(Number(v));

/** One size as the grid edits it; numbers stay strings until the mapper sends them. */
const sizeRowSchema = z.object({
  value: z
    .string()
    .trim()
    .min(1, { error: 'sizeSets:validation.valueRequired' })
    .max(32, { error: 'sizeSets:validation.valueLength' }),
  label: z
    .string()
    .trim()
    .min(1, { error: 'sizeSets:validation.labelRequired' })
    .max(64, { error: 'sizeSets:validation.labelLength' }),
  numericValue: z
    .string()
    .trim()
    .refine((v) => v === '' || isNumber(v), { error: 'sizeSets:validation.number' }),
  /** Same size in another system, keyed by system; empty or missing means none. */
  equivalents: z.record(z.string(), z.string().trim().optional()),
  /** Stored entries that name no system (circumferenceMm, cm); carried through a save untouched. */
  extraEquivalents: z.record(z.string(), z.unknown()).optional(),
});

/**
 * A size set as the editor holds it, following CreateSizeSetBody and UpdateSizeSetBody: key is
 * sent on create only, unit is optional, values replace the whole list on update.
 */
export const sizeSetFormSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1, { error: 'sizeSets:validation.keyRequired' })
    .max(64, { error: 'sizeSets:validation.keyLength' })
    .regex(KEY, { error: 'sizeSets:validation.keyFormat' }),
  name: z
    .string()
    .trim()
    .min(1, { error: 'sizeSets:validation.nameRequired' })
    .max(120, { error: 'sizeSets:validation.nameLength' }),
  system: z.enum(SizeSetSystem),
  unit: z.string().trim().max(16, { error: 'sizeSets:validation.unitLength' }),
  /** A store page handle; blank leaves shoppers on the store-wide size guide. */
  sizeGuideHandle: z
    .string()
    .trim()
    .max(120, { error: 'sizeSets:validation.handleLength' })
    .refine((v) => v === '' || HANDLE.test(v), { error: 'sizeSets:validation.handleFormat' }),
  values: z
    .array(sizeRowSchema)
    .max(MAX_SIZES, { error: 'sizeSets:validation.sizesMax' })
    .superRefine((rows, ctx) => {
      const seen = new Set<string>();
      rows.forEach((row, index) => {
        const value = row.value.trim();
        if (value === '') return;
        if (seen.has(value)) {
          ctx.addIssue({
            code: 'custom',
            path: [index, 'value'],
            message: 'sizeSets:validation.valueDuplicate',
          });
        }
        seen.add(value);
      });
    }),
});

export type SizeSetFormInput = z.input<typeof sizeSetFormSchema>;
export type SizeSetFormValues = z.output<typeof sizeSetFormSchema>;
export type SizeRowInput = SizeSetFormInput['values'][number];

/** The "Add range" helper: from, to and a positive step, at most MAX_SIZES sizes. */
export const sizeRangeSchema = z
  .object({
    from: z.string().trim().refine(isNumber, { error: 'sizeSets:validation.number' }),
    to: z.string().trim().refine(isNumber, { error: 'sizeSets:validation.number' }),
    step: z
      .string()
      .trim()
      .refine((v) => isNumber(v) && Number(v) > 0, { error: 'sizeSets:validation.step' }),
  })
  .superRefine((range, ctx) => {
    if (!isNumber(range.from) || !isNumber(range.to) || !isNumber(range.step)) return;
    const from = Number(range.from);
    const to = Number(range.to);
    const step = Number(range.step);
    if (to < from) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: 'sizeSets:validation.rangeOrder' });
    } else if (step > 0 && Math.floor((to - from) / step + 1e-9) + 1 > MAX_SIZES) {
      ctx.addIssue({ code: 'custom', path: ['step'], message: 'sizeSets:validation.rangeTooLong' });
    }
  });

export type SizeRangeInput = z.input<typeof sizeRangeSchema>;
export type SizeRangeValues = z.output<typeof sizeRangeSchema>;
